import { IonContent, IonHeader, IonItem, IonLabel, IonNote, IonSearchbar, IonPage, IonText, useIonAlert, useIonViewWillEnter, useIonViewWillLeave, IonAccordionGroup, IonAccordion, IonLoading, IonCard, IonCardContent, IonIcon, IonToolbar, IonButtons, IonGrid, IonCol, IonRow, IonButton, IonTitle, IonThumbnail, useIonModal } from "@ionic/react";
import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../services/redux/hook";
import { Contacts, ContactPayload, PhoneType } from '@capacitor-community/contacts';
import { setUserContacts } from "../../services/redux/slices/user-contacts/user-contacts";

import Avatar from 'react-avatar';
import * as Sentry from '@sentry/react';

import './Topup.css'
import { ebioroApi } from "../../services/api/ebioro/ebioro";
import { getTopupProductsParameters, WalletInfoState } from "../../types/types";
import { parsePhoneNumber } from 'awesome-phonenumber';
import { Virtuoso } from 'react-virtuoso';
import { Capacitor } from "@capacitor/core";
import { addOutline, closeOutline } from "ionicons/icons";
import { Keyboard } from "@capacitor/keyboard";
import { setTopUpContact } from "../../services/redux/slices/topup_contact/topup_contact";
import { setTopUpProviders } from "../../services/redux/slices/topup_providers/topup_providers";
import { setTopUpProducts } from "../../services/redux/slices/topup_products/topup_product";
import TopupProvider from "./TopupProvider";
import TopupNewContactPage from "./TopUpNewContact";
import { closeModal, openModal } from '../../services/redux/slices/opened_navigation_modal/opened_root_modal';
import { Provider } from "react-redux";
import { store } from "../../services/redux/store";
import BaseModalComponent from "../../components/BaseModalComponent/BaseModalComponent";
import { useHistory } from "react-router-dom";
const Page: React.FC = () => {
    const pageRef = useRef(null);
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const walletInfo: WalletInfoState = useAppSelector((state) => state.walletInfo);
    const [present] = useIonAlert();
    // const [presentProviderModal, dismissProviderModal] = useIonModal(BaseModalComponent, { component: TopupProvider });
    const [presentNewContactModal, dismissNewContactModal] = useIonModal(BaseModalComponent, { component: TopupNewContactPage });

    const contactsFromRedux: ContactPayload[] = useAppSelector((state) => state.userContacts);

    const [showLoading, setShowLoading] = useState(false);
    const [searchValue, setSearchValue] = useState<string>('');
    const nav = document.querySelector("ion-nav");

    // Memoize filtered contacts for better performance
    const filteredContacts = useMemo(() => {
        if (!searchValue.trim()) return contactsFromRedux;
        return contactsFromRedux.filter((contact) =>
            contact.name?.display?.toLowerCase().includes(searchValue.toLowerCase())
        );
    }, [contactsFromRedux, searchValue]);

    // Remove duplicate phone numbers helper
    const getUniquePhones = useCallback((phones: any[]) => {
        const uniqueIds: string[] = [];
        return phones?.filter(phone => {
            const normalizedNumber = phone.number?.replace(/[^0-9]/g, '');
            const isDuplicate = uniqueIds.includes(normalizedNumber);

            if (!isDuplicate && normalizedNumber) {
                uniqueIds.push(normalizedNumber);
                return true;
            }
            return false;
        }) || [];
    }, []);

    // Filter out contacts with no valid phone numbers to prevent zero-sized elements in Virtuoso
    const contactsWithPhones = useMemo(() => {
        return filteredContacts.filter(contact => {
            const uniquePhones = getUniquePhones(contact.phones || []);
            return uniquePhones.length > 0;
        });
    }, [filteredContacts, getUniquePhones]);

    // const nav = document.querySelector("ion-nav");

    const close = useCallback(() => {
        dispatch(closeModal());
    }, [dispatch]);

    useIonViewWillLeave(() => {
        setSearchValue('');
    });

    useIonViewWillEnter(() => {
        setShowLoading(true);
        getContacts();
    });

    const presentErrorAlert = useCallback((error: string) => {
        present({
            header: "Error",
            message: `${error}`,
            buttons: [
                {
                    text: "Close",
                    role: "cancel",
                    handler: () => {
                        return;
                    },
                },
            ],
        });
    }, [present]);

    const goToTopupProvider = useCallback(async (rawPhoneNumber: string, contactName: string) => {
        if (!rawPhoneNumber?.trim()) {
            presentErrorAlert(t('topups_recipients.page.INVALID-NUMBER'));
            return;
        }

        try {
            setShowLoading(true);

            const providersToDisplay = [];
            const products: any = {};

            const response = await dispatch(
                ebioroApi.endpoints.getToUpLookUp.initiate({
                    accountNumber: rawPhoneNumber.replace(/[+ ]/g, '')
                })
            ).unwrap();

            const pn = parsePhoneNumber(response.account_number_normalized, {
                regionCode: response.country_iso
            });

            if (!pn.valid) {
                presentErrorAlert(t('topups_recipients.page.INVALID-NUMBER'));
                return;
            }

            if (!response.providers?.length) {
                presentErrorAlert(t('topups_recipients.page.PROVIDER-NOT-FOUND'));
                return;
            }

            // Process providers concurrently for better performance
            const providerPromises = response.providers.map(async (provider) => {
                const params: getTopupProductsParameters = {
                    provider_code: provider.provider_code,
                    region_code: provider.country_iso
                };

                try {
                    const productResponse = await dispatch(
                        ebioroApi.endpoints.getToUpProducts.initiate(params)
                    ).unwrap();

                    if (productResponse.length > 0) {
                        const productKey = provider.provider_code;
                        products[productKey] = productResponse;
                        return provider;
                    }
                } catch (error) {
                    console.warn(`Failed to fetch products for provider ${provider.provider_code}:`, error);
                }
                return null;
            });

            const resolvedProviders = await Promise.all(providerPromises);
            const validProviders = resolvedProviders.filter((provider): provider is any => provider !== null);

            if (validProviders.length > 0) {
                dispatch(setTopUpContact({
                    phone_number: pn.number.e164,
                    contact_name: contactName
                }));
                dispatch(setTopUpProviders(validProviders));
                dispatch(setTopUpProducts(products));
                nav?.push(() => <TopupProvider />);

                // dispatch(openModal({ dismissFunc: dismissProviderModal }));

                // setTimeout(() => {
                //     presentProviderModal({
                //         //presentingElement: pageRef.current
                //     });
                // }, 100);
            } else {
                presentErrorAlert(t('topups_recipients.page.PROVIDER-NOT-FOUND'));
            }

        } catch (error: any) {
            console.error('Error in goToTopupProvider:', error);

            if (error.status === 400) {
                presentErrorAlert(t('topups_recipients.page.INVALID-NUMBER'));
            } else {
                Sentry.captureException(error);
                presentErrorAlert(t('topups_recipients.page.GENERIC-ERROR') || 'We have experienced an error. Please try again later');
            }
        } finally {
            setShowLoading(false);
        }
    }, [dispatch, presentErrorAlert, t]);

    const handleChange = useCallback((ev: Event) => {
        const target = ev.target as HTMLIonSearchbarElement;
        setSearchValue(target.value || '');
    }, []);

    const getContacts = useCallback(async () => {
        if (Capacitor.getPlatform() === 'web') {
            // Mock data for web testing
            const contacts: ContactPayload[] = [{
                contactId: "1",
                name: {
                    display: "John Doe",
                    given: "John",
                    middle: "",
                    family: "Doe",
                    prefix: "",
                    suffix: ""
                },
                phones: [{
                    number: "+5352425746", // fake number for testing
                    type: PhoneType.Mobile,
                    label: "work",
                }]
            }];

            dispatch(setUserContacts(contacts));
            setShowLoading(false);
            return;
        }

        try {
            const projection = {
                name: true,
                phones: true
            };

            const result = await Contacts.getContacts({ projection });
            const validContacts = result.contacts
                .filter(contact => contact.name?.display && contact.phones?.length)
                .sort((a, b) => (a.name?.display as string).localeCompare(b.name?.display as string));

            dispatch(setUserContacts(validContacts));
        } catch (error) {
            console.error('Error fetching contacts:', error);
            Sentry.captureException(error);
        } finally {
            setShowLoading(false);
        }
    }, [dispatch]);

    const addNewContact = useCallback(() => {
        dispatch(openModal({ dismissFunc: dismissNewContactModal }));

        setTimeout(() => {
            presentNewContactModal({
                //presentingElement: pageRef.current
            });
        }, 100);
    }, [dispatch, dismissNewContactModal, presentNewContactModal]);

    const handleScrollStart = useCallback(async () => {
        const platform = Capacitor.getPlatform();
        if (platform === 'ios' || platform === 'android') {
            Keyboard.hide();
        }
    }, []);

    useEffect(() => {
        getContacts();
    }, [getContacts]);

    useEffect(() => {
        if (!walletInfo?.account_key) {
            history.replace("/"); // Redirects without keeping history
        }
    }, [walletInfo, history]);

    return (
        <IonPage ref={pageRef}>

            <IonHeader className="ion-no-border">
                <IonToolbar style={{ marginTop: "0%" }}>
                    <IonButtons slot='start'>
                        <IonButton onClick={close} color="primary">
                            <IonIcon slot='start' icon={closeOutline} />
                        </IonButton>

                    </IonButtons>
                    <IonTitle>
                        {t('topups_recipients.page.HEADER') as string}
                    </IonTitle>
                    {/* <IonButtons slot="end" >
                        <IonButton color="primary" onClick={() => { addNewContact() }}>
                            <IonIcon slot="icon-only" icon={addOutline}></IonIcon>
                        </IonButton>
                    </IonButtons> */}
                </IonToolbar>

                <IonToolbar style={{ marginTop: "5%" }} >
                    <IonSearchbar

                        className='ion-text-start search-bar-global'
                        value={searchValue}

                        onIonInput={handleChange}
                        placeholder={t('topups_recipients.page.search.LABEL') as string}
                        debounce={300}
                    >

                    </IonSearchbar>
                </IonToolbar>
            </IonHeader>

            <IonContent scrollY={false}>

                <IonLoading isOpen={showLoading} />

                {contactsWithPhones.length === 0 && !showLoading && (
                    <div className="ion-padding ion-text-center" style={{ marginTop: '50%' }}>
                        <IonText color="medium">
                            <h3>{searchValue ? t('topups_recipients.page.NO_RESULTS') || 'No contacts found' : t('topups_recipients.page.NO_CONTACTS') || 'No contacts available'}</h3>
                        </IonText>
                    </div>
                )}

                <Virtuoso
                    isScrolling={handleScrollStart}
                    style={{ height: '100%' }}
                    totalCount={contactsWithPhones.length}
                    data={contactsWithPhones}

                    itemContent={(index, contact) => {
                        const uniquePhones = getUniquePhones(contact.phones || []);

                        return (
                            <IonCard className="topup_provider_card ion-padding" key={contact.contactId}>
                                <IonCardContent>
                                        {uniquePhones.length > 1 ? (
                                            <IonAccordionGroup>
                                                <IonAccordion>
                                                    <IonItem lines="none" slot="header">
                                                        <IonThumbnail>
                                                            <Avatar
                                                                name={contact.name?.display || 'Unknown'}
                                                                size="45"
                                                                round={true}
                                                            />
                                                        </IonThumbnail>
                                                        <IonLabel>
                                                            <h4>{contact.name?.display || 'Unknown Contact'}</h4>
                                                        </IonLabel>
                                                    </IonItem>

                
                                                        {uniquePhones.map((phone, phoneIndex) => (
                                                            <IonItem
                                                                lines="none"
                                                                key={`${contact.contactId}-${phoneIndex}`}
                                                                onClick={() => goToTopupProvider(
                                                                    phone.number as string,
                                                                    contact.name?.display as string
                                                                )}
                                                                button
                                                            >
                                                                <IonNote className="topup_recipient_note">
                                                                    {phone.number}
                                                                </IonNote>
                                                            </IonItem>
                                                        ))}
                                                    
                                                </IonAccordion>
                                            </IonAccordionGroup>
                                        ) : (
                                            <IonItem
                                                lines="none"
                                                onClick={() => goToTopupProvider(
                                                    uniquePhones[0].number as string,
                                                    contact.name?.display as string
                                                )}
                                                button
                                            >
                                                <IonGrid>
                                                    <IonRow>
                                                        <IonCol size="3">
                                                            <IonThumbnail>
                                                                <Avatar
                                                                    name={contact.name?.display || 'Unknown'}
                                                                    size="45"
                                                                    round={true}
                                                                />
                                                            </IonThumbnail>
                                                        </IonCol>
                                                        <IonCol>
                                                            <IonLabel>
                                                                <h4>{contact.name?.display || 'Unknown Contact'}</h4>
                                                                <IonNote className="topup_recipient_note">
                                                                    {uniquePhones[0].number}
                                                                </IonNote>
                                                            </IonLabel>
                                                        </IonCol>
                                                    </IonRow>
                                                </IonGrid>
                                            </IonItem>
                                        )}
                                    
                                </IonCardContent>
                            </IonCard>
                        );
                    }}
                >

                </Virtuoso>

            </IonContent>


        </IonPage>

    )
}

const TopupRecipient: React.FC = () => (
    <Provider store={store}>
        <Page />
    </Provider>
);

export default TopupRecipient;

// export default React.memo(TopupRecipient);